import React, { useState, useEffect, useMemo } from 'react'
import { supabase } from './supabaseClient'
import { useAuth } from './AuthContext'
import { C } from './theme'
import { CopyButton } from './CopyButton'
import { PORTES_COBRADOS_FALLBACK } from './portes'
import {
  Tag, Search, Download, RefreshCw, X, Home,
  ChevronUp, ChevronDown, Filter, Package,
} from 'lucide-react'

const LS_KEY = 'precos_cliente_filtros_v1'

function readHashParams() {
  if (typeof window === 'undefined') return {}
  const h = window.location.hash
  const q = h.indexOf('?')
  if (q < 0) return {}
  const params = new URLSearchParams(h.slice(q + 1))
  const obj = {}
  for (const [k, v] of params.entries()) obj[k] = v
  return obj
}

function writeHashParams(params) {
  if (typeof window === 'undefined') return
  const base = window.location.hash.replace(/\?.*$/, '')
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null))
  const qs = new URLSearchParams(clean).toString()
  const target = qs ? `${base}?${qs}` : base
  if (window.location.hash !== target) {
    window.history.replaceState(null, '', target)
  }
}

function fmt(n) {
  const v = Number(n || 0)
  return v.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtDate(iso) {
  if (!iso) return '-'
  const d = new Date(iso)
  return d.toLocaleDateString('pt-PT')
}

function csvEscape(v) {
  const s = String(v ?? '')
  if (s.includes(';') || s.includes('"') || s.includes('\n')) {
    return '"' + s.replace(/"/g, '""') + '"'
  }
  return s
}

export default function PrecosCliente() {
  const { isAdmin } = useAuth()

  const initial = useMemo(() => {
    const fromHash = readHashParams()
    let fromLS = {}
    try { fromLS = JSON.parse(localStorage.getItem(LS_KEY) || '{}') } catch { /* noop */ }
    const hoje = new Date()
    const noventa = new Date(); noventa.setDate(noventa.getDate() - 90)
    return {
      cliente: fromHash.cliente ?? fromLS.cliente ?? '',
      dataDe: fromHash.de ?? fromLS.dataDe ?? noventa.toISOString().slice(0, 10),
      dataAte: fromHash.ate ?? fromLS.dataAte ?? hoje.toISOString().slice(0, 10),
      numeroEnc: fromHash.enc ?? fromLS.numeroEnc ?? '',
      produto: fromHash.produto ?? fromLS.produto ?? '',
      estado: fromHash.estado ?? fromLS.estado ?? '',
    }
  }, [])

  const [filtros, setFiltros] = useState(initial)
  const [loading, setLoading] = useState(true)
  const [errorMsg, setErrorMsg] = useState('')
  const [itens, setItens] = useState([])
  const [encomendas, setEncomendas] = useState([])
  const [perfis, setPerfis] = useState([])
  const [produtos, setProdutos] = useState([])
  const [sortBy, setSortBy] = useState('data_desc')
  const [aba, setAba] = useState('historico')        // 'historico' | 'tabela'
  const [soAssociados, setSoAssociados] = useState(false)

  useEffect(() => {
    writeHashParams({
      cliente: filtros.cliente,
      de: filtros.dataDe,
      ate: filtros.dataAte,
      enc: filtros.numeroEnc,
      produto: filtros.produto,
      estado: filtros.estado,
    })
    try { localStorage.setItem(LS_KEY, JSON.stringify(filtros)) } catch { /* noop */ }
  }, [filtros])

  const carregar = async () => {
    setLoading(true)
    setErrorMsg('')
    try {
      const [encRes, itensRes, perfisRes, prodRes] = await Promise.all([
        supabase.from('encomendas')
          .select('id, numero_encomenda, cliente_id, cliente_email, cliente_nome, total_sem_iva, total_com_iva, total_final, desconto_percentagem, desconto_valor, estado, created_at, pago_1a_prestacao, pago_2a_prestacao, tracking')
          .order('created_at', { ascending: false }),
        supabase.from('encomenda_itens')
          .select('id, encomenda_id, produto_nome, produto_ean, quantidade, preco_unitario, total_sem_iva, created_at'),
        supabase.from('perfis')
          .select('id, nome, email, cliente_interno, tipo_cliente, role, ativo, estado_conta, desconto_percentagem'),
        supabase.from('Produtos')
          .select('EAN, Nome, preco, preco_mercado, Stock_Atual, qtd_minima_compra, visivel_catalogo, "Email_Cliente", categoria, "Transito1_Qtd", "Transito1_Data", "Transito2_Qtd", "Transito2_Data", "Transito3_Qtd", "Transito3_Data", "Transito4_Qtd", "Transito4_Data", "Transito5_Qtd", "Transito5_Data"'),
      ])
      if (encRes.error) throw encRes.error
      if (itensRes.error) throw itensRes.error
      if (perfisRes.error) throw perfisRes.error
      if (prodRes.error) throw prodRes.error
      setEncomendas(encRes.data || [])
      setItens(itensRes.data || [])
      setPerfis(perfisRes.data || [])
      setProdutos(prodRes.data || [])
    } catch (e) {
      setErrorMsg(e?.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { carregar() }, [])

  const perfilPorId = useMemo(() => {
    const m = new Map()
    for (const p of perfis) m.set(p.id, p)
    return m
  }, [perfis])

  const clientesDropdown = useMemo(() => {
    const usados = new Map()
    for (const e of encomendas) {
      if (!e.cliente_id) continue
      if (!usados.has(e.cliente_id)) {
        const perf = perfilPorId.get(e.cliente_id)
        usados.set(e.cliente_id, perf?.nome || e.cliente_nome || e.cliente_email)
      }
    }
    return Array.from(usados.entries())
      .map(([id, nome]) => ({ id, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt'))
  }, [encomendas, perfilPorId])

  // ─── Tabela de preços do cliente ────────────────────────────────────────
  // Parte do CATÁLOGO, não das encomendas: mostra tudo o que o cliente pode
  // comprar, tenha comprado ou não. O preço é sempre lido de Produtos.preco,
  // por isso qualquer alteração no Catálogo aparece aqui de imediato — não há
  // cópia de preço guardada em lado nenhum.
  // Portes já incluídos em Produtos.preco pela fórmula; o desconto não incide neles.
  // Vem de src/portes.js para não ficar um 1.00 solto aqui que deixe de bater
  // com o resto da app se o valor mudar.
  const PORTES_EMBUTIDOS = PORTES_COBRADOS_FALLBACK

  // Todos os clientes activos, não só os que já encomendaram — senão não dava
  // para preparar proposta a cliente novo, que é quando isto mais serve.
  const clientesTodos = useMemo(() => {
    return perfis
      .filter(p => p.role === 'cliente' && p.ativo !== false && p.estado_conta !== 'recusado')
      .map(p => ({ id: p.id, nome: p.nome || p.email, email: p.email, desconto: Number(p.desconto_percentagem || 0) }))
      .sort((a, b) => String(a.nome).localeCompare(String(b.nome), 'pt'))
  }, [perfis])

  // Última compra por produto, para a coluna "já comprou".
  //
  // ATENÇÃO: encomenda_itens.preco_unitario guarda o preço DE TABELA da altura,
  // não o que o cliente pagou — o desconto dele é aplicado ao total da encomenda,
  // não linha a linha. Mostrar esse valor em bruto ao lado do "preço dele" (que já
  // tem desconto) compara bases diferentes e faz parecer que o preço baixou quando
  // afinal subiu. Aplica-se aqui o mesmo desconto pro-rata que o separador do
  // histórico já usa, para os dois números serem comparáveis.
  const ultimaCompraPorProduto = useMemo(() => {
    const cli = filtros.cliente
    const m = new Map()
    if (!cli) return m
    const encsDoCliente = new Map(encomendas.filter(e => e.cliente_id === cli).map(e => [e.id, e]))
    for (const it of itens) {
      const enc = encsDoCliente.get(it.encomenda_id)
      if (!enc) continue
      const chave = String(it.produto_nome || '').trim().toLowerCase()
      const anterior = m.get(chave)
      if (anterior && new Date(it.created_at) <= new Date(anterior.data)) continue

      const tabelaNaAltura = Number(it.preco_unitario || 0)
      const descValor = Number(enc.desconto_valor || 0)
      const encTotal = Number(enc.total_sem_iva || 0)
      const ratio = (descValor > 0 && encTotal > 0) ? Math.max(0, (encTotal - descValor) / encTotal) : 1
      m.set(chave, {
        data: it.created_at,
        preco: tabelaNaAltura * ratio,   // o que ele pagou mesmo, por unidade
        tabelaNaAltura,                  // o preço de tabela dessa altura, para comparar evolução
        qtd: Number(it.quantidade || 0),
      })
    }
    return m
  }, [itens, encomendas, filtros.cliente])

  const tabelaCliente = useMemo(() => {
    const cli = clientesTodos.find(c => c.id === filtros.cliente)
    if (!cli) return { linhas: [], cliente: null }
    const emailCli = String(cli.email || '').trim().toLowerCase()
    const d = cli.desconto / 100
    const q = String(filtros.produto || '').trim().toLowerCase()

    const linhas = produtos
      .filter(p => {
        const dono = p.Email_Cliente ? String(p.Email_Cliente).trim().toLowerCase() : null
        const meu = dono === emailCli
        // Mesma regra do catálogo: os dele, ou públicos e visíveis. Nunca os de outro cliente.
        if (!(meu || (dono === null && p.visivel_catalogo))) return false
        if (soAssociados && !meu) return false
        if (q && !String(p.Nome || '').toLowerCase().includes(q) && !String(p.EAN || '').toLowerCase().includes(q)) return false
        return Number(p.preco) > 0
      })
      .map(p => {
        const tabela = Number(p.preco)
        // O desconto não incide sobre os portes já embutidos no preço
        const final = (tabela - PORTES_EMBUTIDOS) * (1 - d) + PORTES_EMBUTIDOS
        const mercado = Number(p.preco_mercado) > 0 ? Number(p.preco_mercado) : null
        const ultima = ultimaCompraPorProduto.get(String(p.Nome || '').trim().toLowerCase()) || null

        // Encomendas a caminho. Inclui as de data já passada — um trânsito atrasado
        // continua a ser mercadoria comprada, e escondê-lo dava a ideia errada de
        // que não vem nada. Marca-se como atrasado em vez de se ignorar.
        const hoje = new Date(); hoje.setHours(0, 0, 0, 0)
        const transitos = [1, 2, 3]
          .map(n => ({ qtd: Number(p[`Transito${n}_Qtd`] || 0), data: p[`Transito${n}_Data`] }))
          .filter(t => t.qtd > 0 && t.data)
          .sort((a, b) => String(a.data).localeCompare(String(b.data)))
        const caminho = transitos.length
          ? {
              qtd: transitos.reduce((s, t) => s + t.qtd, 0),
              proxima: transitos[0].data,
              atrasado: new Date(new Date(transitos[0].data).setHours(0, 0, 0, 0)) < hoje,
            }
          : null
        return {
          nome: p.Nome, ean: p.EAN, categoria: p.categoria,
          meu: (p.Email_Cliente ? String(p.Email_Cliente).trim().toLowerCase() : null) === emailCli,
          tabela, final, poupanca: tabela - final,
          stock: Number(p.Stock_Atual || 0), caminho,
          qtdMin: Number(p.qtd_minima_compra || 10),
          mercado,
          // Margem que ELE consegue se vender ao preço de mercado
          margemRevenda: mercado != null && mercado > 0 ? (mercado - final) / mercado * 100 : null,
          ultima,
        }
      })
      .sort((a, b) => Number(b.meu) - Number(a.meu) || String(a.nome).localeCompare(String(b.nome), 'pt'))

    return { linhas, cliente: cli }
  }, [produtos, clientesTodos, filtros.cliente, filtros.produto, soAssociados, ultimaCompraPorProduto])

  const precoActualPorEan = useMemo(() => {
    const m = new Map()
    for (const p of produtos) {
      if (p?.EAN) m.set(String(p.EAN).trim(), Number(p.preco || 0))
    }
    return m
  }, [produtos])

  const precoActualPorNome = useMemo(() => {
    const m = new Map()
    for (const p of produtos) {
      if (p?.Nome) m.set(String(p.Nome).trim().toLowerCase(), Number(p.preco || 0))
    }
    return m
  }, [produtos])

  const rows = useMemo(() => {
    const encPorId = new Map(encomendas.map(e => [e.id, e]))
    const out = []
    for (const it of itens) {
      const enc = encPorId.get(it.encomenda_id)
      if (!enc) continue
      const perf = perfilPorId.get(enc.cliente_id)
      const isInterno = !!perf?.cliente_interno
      const isParticular = perf?.tipo_cliente === 'particular'
      const totalSemIvaTabela = Number(it.total_sem_iva || 0)
      const qtd = Number(it.quantidade || 0)
      const factorIva = isInterno ? 1 : 1.23

      // Aplicar desconto pro-rata: preco efectivo por linha reflecte o desconto da encomenda
      const descPct = Number(enc.desconto_percentagem || 0)
      const descValor = Number(enc.desconto_valor || 0)
      const encTotalSemIva = Number(enc.total_sem_iva || 0)
      const ratio = (descValor > 0 && encTotalSemIva > 0)
        ? Math.max(0, (encTotalSemIva - descValor) / encTotalSemIva)
        : 1

      const precoUnitSemIvaTabela = qtd > 0 ? totalSemIvaTabela / qtd : Number(it.preco_unitario || 0)
      const totalSemIvaEfectivo = totalSemIvaTabela * ratio
      const totalComIvaEfectivo = totalSemIvaEfectivo * factorIva
      const precoUnitSemIvaEfectivo = qtd > 0 ? totalSemIvaEfectivo / qtd : precoUnitSemIvaTabela * ratio
      const precoUnitComIvaEfectivo = precoUnitSemIvaEfectivo * factorIva

      // Preco actual no catalogo (base s/IVA, sem descontos/portes)
      const eanKey = String(it.produto_ean || '').trim()
      const nomeKey = String(it.produto_nome || '').trim().toLowerCase()
      let precoActual = null
      if (eanKey && precoActualPorEan.has(eanKey)) precoActual = precoActualPorEan.get(eanKey)
      else if (nomeKey && precoActualPorNome.has(nomeKey)) precoActual = precoActualPorNome.get(nomeKey)
      const deltaAbs = precoActual != null ? (precoActual - precoUnitSemIvaTabela) : null
      const deltaPct = (precoActual != null && precoUnitSemIvaTabela > 0)
        ? (deltaAbs / precoUnitSemIvaTabela) * 100
        : null

      out.push({
        item_id: it.id,
        encomenda_id: enc.id,
        numero_encomenda: enc.numero_encomenda,
        data: enc.created_at,
        cliente_id: enc.cliente_id,
        cliente_nome: perf?.nome || enc.cliente_nome || enc.cliente_email,
        cliente_email: enc.cliente_email,
        is_interno: isInterno,
        is_particular: isParticular,
        produto_nome: it.produto_nome,
        produto_ean: it.produto_ean || '',
        quantidade: qtd,
        desc_pct: descPct,
        // Valores TABELA (histórico do preço no catálogo à altura, sem desconto)
        preco_unit_sem_iva_tabela: precoUnitSemIvaTabela,
        total_sem_iva_tabela: totalSemIvaTabela,
        // Valores EFECTIVOS (com desconto aplicado pro-rata + IVA final)
        preco_unit_sem_iva: precoUnitSemIvaEfectivo,
        preco_unit_com_iva: precoUnitComIvaEfectivo,
        total_sem_iva: totalSemIvaEfectivo,
        total_com_iva: totalComIvaEfectivo,
        // Comparacao com preco actual do catalogo
        preco_actual: precoActual,
        delta_abs: deltaAbs,
        delta_pct: deltaPct,
        estado: enc.estado,
        pago_1a: !!enc.pago_1a_prestacao,
        pago_2a: !!enc.pago_2a_prestacao,
        tracking: enc.tracking,
      })
    }
    return out
  }, [encomendas, itens, perfilPorId, precoActualPorEan, precoActualPorNome])

  const filtradas = useMemo(() => {
    const clienteF = filtros.cliente
    const de = filtros.dataDe
    const ate = filtros.dataAte
    const encF = (filtros.numeroEnc || '').trim().toLowerCase()
    const prodF = (filtros.produto || '').trim().toLowerCase()
    const estadoF = filtros.estado
    return rows.filter(r => {
      if (clienteF && r.cliente_id !== clienteF) return false
      const dataDia = String(r.data || '').slice(0, 10)
      if (de && dataDia < de) return false
      if (ate && dataDia > ate) return false
      if (encF && !String(r.numero_encomenda || '').toLowerCase().includes(encF)) return false
      if (prodF) {
        const nome = String(r.produto_nome || '').toLowerCase()
        const ean = String(r.produto_ean || '').toLowerCase()
        if (!nome.includes(prodF) && !ean.includes(prodF)) return false
      }
      if (estadoF) {
        if (estadoF === 'pago_100' && !(r.pago_1a && r.pago_2a)) return false
        if (estadoF === 'pago_50' && !(r.pago_1a && !r.pago_2a)) return false
        if (estadoF === 'pendente' && r.pago_1a) return false
        if (estadoF === 'enviado' && r.estado !== 'enviado') return false
      }
      return true
    })
  }, [rows, filtros])

  const ordenadas = useMemo(() => {
    const arr = filtradas.slice()
    switch (sortBy) {
      case 'data_asc': arr.sort((a, b) => String(a.data).localeCompare(String(b.data))); break
      case 'data_desc': arr.sort((a, b) => String(b.data).localeCompare(String(a.data))); break
      case 'cliente': arr.sort((a, b) => (a.cliente_nome || '').localeCompare(b.cliente_nome || '', 'pt')); break
      case 'produto': arr.sort((a, b) => (a.produto_nome || '').localeCompare(b.produto_nome || '', 'pt')); break
      case 'qtd_desc': arr.sort((a, b) => b.quantidade - a.quantidade); break
      case 'total_desc': arr.sort((a, b) => b.total_com_iva - a.total_com_iva); break
      case 'total_asc': arr.sort((a, b) => a.total_com_iva - b.total_com_iva); break
      default: break
    }
    return arr
  }, [filtradas, sortBy])

  const totais = useMemo(() => {
    let sIva = 0, cIva = 0, un = 0
    for (const r of ordenadas) { sIva += r.total_sem_iva; cIva += r.total_com_iva; un += r.quantidade }
    return { sIva, cIva, un, linhas: ordenadas.length }
  }, [ordenadas])

  const exportarCsv = () => {
    const header = ['Data', 'Cliente', 'Interno', 'NºEncomenda', 'Produto', 'EAN', 'Qtd', 'Desc%', 'UnitSemIVATabela', 'UnitSemIVAEfectivo', 'UnitComIVAEfectivo', 'PrecoActualCatalogo', 'DeltaAbs', 'DeltaPct', 'TotalSemIVATabela', 'TotalSemIVAEfectivo', 'TotalComIVAEfectivo', 'Estado']
    const linhas = ordenadas.map(r => [
      fmtDate(r.data),
      r.cliente_nome,
      r.is_interno ? 'sim' : 'nao',
      r.numero_encomenda,
      r.produto_nome,
      r.produto_ean,
      r.quantidade,
      r.desc_pct || 0,
      r.preco_unit_sem_iva_tabela.toFixed(2),
      r.preco_unit_sem_iva.toFixed(2),
      r.preco_unit_com_iva.toFixed(2),
      r.preco_actual != null ? r.preco_actual.toFixed(2) : '',
      r.delta_abs != null ? r.delta_abs.toFixed(2) : '',
      r.delta_pct != null ? r.delta_pct.toFixed(1) : '',
      r.total_sem_iva_tabela.toFixed(2),
      r.total_sem_iva.toFixed(2),
      r.total_com_iva.toFixed(2),
      r.estado || '',
    ].map(csvEscape).join(';'))
    const bom = '﻿'
    const conteudo = bom + [header.join(';'), ...linhas].join('\n')
    const blob = new Blob([conteudo], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `precos-cliente-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  const limparFiltros = () => {
    const hoje = new Date()
    const noventa = new Date(); noventa.setDate(noventa.getDate() - 90)
    setFiltros({
      cliente: '',
      dataDe: noventa.toISOString().slice(0, 10),
      dataAte: hoje.toISOString().slice(0, 10),
      numeroEnc: '',
      produto: '',
      estado: '',
    })
  }

  if (!isAdmin) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
        <div style={{ textAlign: 'center', color: C.textMuted }}>
          <Package size={40} style={{ opacity: 0.4 }} />
          <p style={{ marginTop: '12px', fontSize: '14px' }}>Acesso restrito ao administrador.</p>
        </div>
      </div>
    )
  }

  const inputBase = {
    padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: '4px',
    fontSize: '13px', background: '#fff', color: C.text, outline: 'none',
    minHeight: '36px',
  }
  const labelBase = { fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: '4px' }
  const thBase = {
    padding: '10px 12px', textAlign: 'left', fontSize: '10px', fontWeight: '700',
    color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em',
    background: C.bg, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap',
    position: 'sticky', top: 0, zIndex: 1,
  }
  const tdBase = { padding: '10px 12px', fontSize: '13px', color: C.text, borderBottom: `1px solid ${C.borderLight}`, verticalAlign: 'top' }

  const sortHeader = (label, key) => {
    const active = sortBy === key || sortBy === `${key}_desc` || sortBy === `${key}_asc`
    const dir = sortBy === `${key}_desc` ? 'desc' : sortBy === `${key}_asc` ? 'asc' : null
    return (
      <th style={thBase} onClick={() => {
        if (key === 'data') setSortBy(sortBy === 'data_desc' ? 'data_asc' : 'data_desc')
        else if (key === 'total') setSortBy(sortBy === 'total_desc' ? 'total_asc' : 'total_desc')
        else setSortBy(key)
      }}>
        <span style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          {label}
          {active && dir === 'desc' && <ChevronDown size={11} />}
          {active && dir === 'asc' && <ChevronUp size={11} />}
        </span>
      </th>
    )
  }

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
          <Tag size={20} color={C.primary} />
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: C.text, margin: 0 }}>Preços Cliente</h1>
        </div>
        <p style={{ fontSize: '13px', color: C.textMuted, margin: '0 0 14px' }}>
          {aba === 'historico'
            ? 'Histórico item-a-item de compras dos clientes. Usa para consultar rapidamente o preço pago quando estás a definir preço de venda no marketplace.'
            : 'Tudo o que este cliente pode comprar, tenha comprado ou não, com o preço final dele já com desconto e portes. Sempre sem IVA.'}
        </p>

        {/* Abas */}
        <div style={{ display: 'flex', gap: '2px', borderBottom: `1px solid ${C.border}`, marginBottom: '14px' }}>
          {[
            { id: 'historico', label: 'Histórico de compras' },
            { id: 'tabela', label: 'Tabela de preços do cliente' },
          ].map(t => (
            <button key={t.id} onClick={() => setAba(t.id)}
              style={{
                padding: '9px 16px', border: 'none', background: 'transparent', cursor: 'pointer',
                fontSize: '13px', fontWeight: aba === t.id ? '800' : '600',
                color: aba === t.id ? C.text : C.textMuted,
                borderBottom: `2px solid ${aba === t.id ? C.primary : 'transparent'}`,
                marginBottom: '-1px',
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Filtros */}
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: '6px', padding: '14px', marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Filter size={14} color={C.textMuted} />
            <span style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Filtros</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
            <div>
              <label style={labelBase}>Cliente</label>
              <select
                value={filtros.cliente}
                onChange={e => setFiltros(f => ({ ...f, cliente: e.target.value }))}
                style={{ ...inputBase, width: '100%' }}
              >
                {/* Na tabela de preços tem de haver um cliente escolhido — o preço depende do desconto dele */}
                <option value="">{aba === 'tabela' ? '— Escolhe um cliente —' : '— Todos —'}</option>
                {(aba === 'tabela' ? clientesTodos : clientesDropdown).map(c => (
                  <option key={c.id} value={c.id}>{c.nome}</option>
                ))}
              </select>
            </div>
            {aba === 'tabela' && (
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', color: C.text, cursor: 'pointer', paddingBottom: '7px' }}>
                  <input type="checkbox" checked={soAssociados} onChange={e => setSoAssociados(e.target.checked)}
                    style={{ cursor: 'pointer', accentColor: C.primary, width: '15px', height: '15px' }} />
                  Só produtos associados a ele
                </label>
              </div>
            )}
            <div>
              <label style={labelBase}>Data de</label>
              <input type="date" value={filtros.dataDe} onChange={e => setFiltros(f => ({ ...f, dataDe: e.target.value }))} style={{ ...inputBase, width: '100%' }} />
            </div>
            <div>
              <label style={labelBase}>Data até</label>
              <input type="date" value={filtros.dataAte} onChange={e => setFiltros(f => ({ ...f, dataAte: e.target.value }))} style={{ ...inputBase, width: '100%' }} />
            </div>
            <div>
              <label style={labelBase}>Nº Encomenda</label>
              <input type="text" value={filtros.numeroEnc} onChange={e => setFiltros(f => ({ ...f, numeroEnc: e.target.value }))} placeholder="0007/2026" style={{ ...inputBase, width: '100%' }} />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={labelBase}>Produto ou EAN</label>
              <div style={{ position: 'relative' }}>
                <Search size={13} color={C.textMuted} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                <input type="text" value={filtros.produto} onChange={e => setFiltros(f => ({ ...f, produto: e.target.value }))} placeholder="dior, 33489..." style={{ ...inputBase, width: '100%', paddingLeft: '30px' }} />
              </div>
            </div>
            <div>
              <label style={labelBase}>Estado</label>
              <select value={filtros.estado} onChange={e => setFiltros(f => ({ ...f, estado: e.target.value }))} style={{ ...inputBase, width: '100%' }}>
                <option value="">— Todos —</option>
                <option value="pago_100">100% Pago</option>
                <option value="pago_50">50% Pago</option>
                <option value="pendente">Pendente</option>
                <option value="enviado">Enviado</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
            <button onClick={limparFiltros} style={{ padding: '7px 12px', border: `1px solid ${C.border}`, background: '#fff', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: C.text, display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <X size={12} /> Limpar filtros
            </button>
            <button onClick={carregar} disabled={loading} style={{ padding: '7px 12px', border: `1px solid ${C.border}`, background: '#fff', borderRadius: '4px', cursor: loading ? 'wait' : 'pointer', fontSize: '12px', color: C.text, display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <RefreshCw size={12} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} /> Recarregar
            </button>
            <div style={{ flex: 1 }} />
            <button onClick={exportarCsv} disabled={ordenadas.length === 0} style={{ padding: '7px 14px', border: 'none', background: C.primary, color: '#fff', borderRadius: '4px', cursor: ordenadas.length === 0 ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '5px', opacity: ordenadas.length === 0 ? 0.5 : 1 }}>
              <Download size={12} /> Exportar CSV
            </button>
          </div>
        </div>

        {/* ═══ TABELA DE PREÇOS DO CLIENTE ═══ */}
        {aba === 'tabela' && (
          !tabelaCliente.cliente ? (
            <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: '6px', padding: '50px 20px', textAlign: 'center', color: C.textMuted }}>
              <Tag size={40} style={{ opacity: 0.25, marginBottom: '10px' }} />
              <p style={{ margin: 0, fontSize: '14px' }}>Escolhe um cliente no filtro acima.</p>
              <p style={{ margin: '4px 0 0', fontSize: '12px' }}>O preço depende do desconto dele, por isso não dá para mostrar sem saber quem é.</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap', marginBottom: '10px', padding: '10px 14px', background: C.bg, borderRadius: '4px', fontSize: '12px' }}>
                <span style={{ color: C.textMuted }}>📊 <strong style={{ color: C.text }}>{tabelaCliente.linhas.length}</strong> produtos disponíveis</span>
                <span style={{ color: C.textMuted }}>◆ <strong style={{ color: '#107c10' }}>{tabelaCliente.linhas.filter(l => l.meu).length}</strong> exclusivos dele</span>
                <span style={{ color: C.textMuted }}>Desconto: <strong style={{ color: C.text }}>{tabelaCliente.cliente.desconto}%</strong></span>
                <span style={{ color: C.textMuted, fontStyle: 'italic' }}>Preços sempre s/IVA, com portes de 1 € já incluídos</span>
              </div>

              <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: '6px', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 360px)', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1140px' }}>
                    <thead>
                      <tr style={{ background: C.bg, position: 'sticky', top: 0, zIndex: 1 }}>
                        {['Produto', 'Preço tabela', 'PREÇO DELE', 'Poupa', 'Stock', 'A caminho', 'Mín.', 'Mercado', 'Margem revenda', 'Última compra'].map((h, i) => (
                          <th key={h} style={{ padding: '9px 10px', textAlign: i === 0 ? 'left' : 'right', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tabelaCliente.linhas.map((l, i) => (
                        <tr key={l.nome + i} style={{ borderBottom: `1px solid ${C.border}`, background: l.meu ? '#f0fdf4' : 'transparent' }}>
                          <td style={{ padding: '8px 10px', maxWidth: '340px' }}>
                            <div style={{ fontSize: '12.5px', fontWeight: '600', color: C.text, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              {l.meu && <span title="Exclusivo deste cliente" style={{ padding: '1px 5px', borderRadius: '3px', fontSize: '9px', fontWeight: '800', background: '#107c10', color: '#fff' }}>DELE</span>}
                              {l.nome}
                              {l.nome && <CopyButton text={l.nome} size={11} title="Copiar nome do artigo" />}
                            </div>
                            {l.ean && (
                              <div style={{ fontSize: '10px', color: C.textMuted, fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                {l.ean}
                                <CopyButton text={l.ean} size={11} title="Copiar EAN" />
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: C.textMuted, whiteSpace: 'nowrap' }}>{fmt(l.tabela)} €</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '800', fontSize: '14px', color: C.text, whiteSpace: 'nowrap' }}>{fmt(l.final)} €</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: l.poupanca > 0 ? '#107c10' : C.textMuted, whiteSpace: 'nowrap' }}>
                            {l.poupanca > 0 ? `−${fmt(l.poupanca)} €` : '—'}
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: l.stock > 0 ? C.text : C.danger, fontWeight: l.stock > 0 ? '400' : '700', whiteSpace: 'nowrap' }}>
                            {l.stock > 0 ? `${l.stock} un` : 'esgotado'}
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {l.caminho ? (
                              <span title={l.caminho.atrasado
                                ? `Chegada prevista para ${fmtDate(l.caminho.proxima)} — já passou. Confirmar com o fornecedor.`
                                : `Próxima chegada a ${fmtDate(l.caminho.proxima)}`}>
                                <span style={{ fontWeight: '700', color: C.text }}>+{l.caminho.qtd} un</span>
                                <span style={{ display: 'block', fontSize: '10px', color: l.caminho.atrasado ? C.danger : C.textMuted }}>
                                  {l.caminho.atrasado ? `⚠ ${fmtDate(l.caminho.proxima)}` : fmtDate(l.caminho.proxima)}
                                </span>
                              </span>
                            ) : (
                              <span style={{ color: C.textFaint }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: C.textMuted, whiteSpace: 'nowrap' }}>{l.qtdMin} un</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: C.textMuted, whiteSpace: 'nowrap' }}>{l.mercado != null ? `${fmt(l.mercado)} €` : '—'}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: '700', color: l.margemRevenda == null ? C.textMuted : (l.margemRevenda >= 25 ? '#107c10' : l.margemRevenda >= 10 ? C.warning : C.danger) }}>
                            {l.margemRevenda != null ? `${l.margemRevenda.toFixed(1)}%` : '—'}
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap', fontSize: '11px' }}>
                            {l.ultima ? (() => {
                              const dif = l.final - l.ultima.preco   // + = paga mais hoje
                              return (
                                <span title={`Pagou ${fmt(l.ultima.preco)} €/un (tabela dessa altura: ${fmt(l.ultima.tabelaNaAltura)} €). Hoje pagaria ${fmt(l.final)} €/un.`}>
                                  <span style={{ color: C.textMuted }}>{fmt(l.ultima.preco)} €</span>
                                  {Math.abs(dif) >= 0.01 && (
                                    <span style={{ color: dif > 0 ? '#a4262c' : '#107c10', fontWeight: '700' }}>
                                      {dif > 0 ? ' ▲' : ' ▼'}{fmt(Math.abs(dif))}
                                    </span>
                                  )}
                                  <span style={{ display: 'block', fontSize: '10px', color: C.textFaint }}>
                                    {new Date(l.ultima.data).toLocaleDateString('pt-PT')}
                                  </span>
                                </span>
                              )
                            })() : <span style={{ color: C.textFaint }}>nunca comprou</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <p style={{ fontSize: '11px', color: C.textMuted, margin: '10px 0 20px', lineHeight: '1.6' }}>
                <strong>Preço dele</strong> = (preço de tabela − 1 € de portes) × (1 − {tabelaCliente.cliente.desconto}%) + 1 €. O desconto não incide sobre os portes.
                {' '}<strong>Margem revenda</strong> = o que ele ganha se vender ao preço de mercado — só aparece nos produtos que têm preço de mercado registado.
                {' '}Os preços vêm sempre do Catálogo: se lá mudares um preço, muda aqui de imediato.
              </p>
            </>
          )
        )}

        {/* Contador + totais */}
        {aba === 'historico' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap', marginBottom: '10px', padding: '10px 14px', background: C.bg, borderRadius: '4px', fontSize: '12px' }}>
          <span style={{ color: C.textMuted }}>📊 <strong style={{ color: C.text }}>{totais.linhas}</strong> {totais.linhas === 1 ? 'linha' : 'linhas'}</span>
          <span style={{ color: C.textMuted }}>📦 <strong style={{ color: C.text }}>{totais.un}</strong> un</span>
          <span style={{ color: C.textMuted }}>Total s/IVA: <strong style={{ color: C.text }}>{fmt(totais.sIva)} €</strong></span>
          <span style={{ color: C.textMuted }}>Total c/IVA: <strong style={{ color: '#107c10' }}>{fmt(totais.cIva)} €</strong></span>
        </div>
        )}

        {/* Tabela do historico */}
        {aba === 'historico' && (<>
        {errorMsg && (
          <div style={{ padding: '12px', background: '#fef2f2', border: '1px solid #ef4444', borderRadius: '4px', color: '#b91c1c', fontSize: '13px', marginBottom: '10px' }}>
            Erro: {errorMsg}
          </div>
        )}
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: '6px', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 360px)', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
              <thead>
                <tr>
                  {sortHeader('Data', 'data')}
                  {sortHeader('Cliente', 'cliente')}
                  <th style={thBase}>Nº Enc</th>
                  {sortHeader('Produto', 'produto')}
                  <th style={thBase}>EAN</th>
                  <th style={{ ...thBase, textAlign: 'right' }}>Qtd</th>
                  <th style={{ ...thBase, textAlign: 'right' }} title="Desconto aplicado a esta encomenda (afecta os precos efectivos abaixo)">Desc%</th>
                  <th style={{ ...thBase, textAlign: 'right' }} title="Preco unitario efectivo apos desconto pro-rata">€/un s/IVA</th>
                  <th style={{ ...thBase, textAlign: 'right' }} title="Preco unitario efectivo com IVA">€/un c/IVA</th>
                  <th style={{ ...thBase, textAlign: 'right' }} title="Total da linha apos desconto pro-rata">Total s/IVA</th>
                  <th style={{ ...thBase, textAlign: 'right', cursor: 'pointer' }} onClick={() => setSortBy(sortBy === 'total_desc' ? 'total_asc' : 'total_desc')}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      Total c/IVA
                      {sortBy === 'total_desc' && <ChevronDown size={11} />}
                      {sortBy === 'total_asc' && <ChevronUp size={11} />}
                    </span>
                  </th>
                  <th style={thBase}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={12} style={{ ...tdBase, textAlign: 'center', color: C.textMuted, padding: '30px' }}>A carregar…</td></tr>
                ) : ordenadas.length === 0 ? (
                  <tr><td colSpan={12} style={{ ...tdBase, textAlign: 'center', color: C.textMuted, padding: '30px' }}>Nenhuma linha para os filtros actuais.</td></tr>
                ) : ordenadas.map(r => {
                  const estadoBadge = r.pago_1a && r.pago_2a
                    ? { txt: '100% Pago', bg: '#dff6dd', fg: '#107c10' }
                    : r.pago_1a
                    ? { txt: '50% Pago', bg: '#fff4ce', fg: '#c07400' }
                    : { txt: 'Pendente', bg: '#fde7e9', fg: '#a4262c' }
                  const temDesc = Number(r.desc_pct || 0) > 0
                  const tooltipUnit = temDesc ? `Tabela: ${fmt(r.preco_unit_sem_iva_tabela)} € s/IVA (antes do desconto de ${r.desc_pct}%)` : ''
                  const tooltipTotal = temDesc ? `Tabela: ${fmt(r.total_sem_iva_tabela)} € s/IVA (antes do desconto de ${r.desc_pct}%)` : ''
                  return (
                    <tr key={r.item_id} style={{ background: r.is_interno ? '#fffbe6' : '#fff' }}>
                      <td style={tdBase}>{fmtDate(r.data)}</td>
                      <td style={tdBase}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>{r.cliente_nome}</span>
                          {r.is_interno && <span title="Cliente interno · preço = custo, não venda a lucrar" style={{ display: 'inline-flex', alignItems: 'center', padding: '1px 5px', background: '#fef3c7', color: '#92400e', borderRadius: '3px', fontSize: '10px', fontWeight: '700', gap: '3px' }}><Home size={9} /> INTERNO</span>}
                        </div>
                      </td>
                      <td style={{ ...tdBase, fontFamily: 'monospace', fontSize: '12px' }}>{r.numero_encomenda}</td>
                      <td style={tdBase}>{r.produto_nome}</td>
                      <td style={{ ...tdBase, fontFamily: 'monospace', fontSize: '11px', color: C.textMuted }}>{r.produto_ean || '-'}</td>
                      <td style={{ ...tdBase, textAlign: 'right' }}>{r.quantidade}</td>
                      <td style={{ ...tdBase, textAlign: 'right', fontFamily: 'monospace', color: temDesc ? '#c07400' : C.textMuted, fontWeight: temDesc ? '700' : '400' }}>
                        {temDesc ? `-${r.desc_pct}%` : '—'}
                      </td>
                      <td style={{ ...tdBase, textAlign: 'right', fontFamily: 'monospace' }} title={tooltipUnit}>
                        <div>{fmt(r.preco_unit_sem_iva)} €</div>
                        {r.preco_actual != null && (() => {
                          const subiu = r.delta_abs >= 0.05
                          const desceu = r.delta_abs <= -0.05
                          const cor = subiu ? '#a4262c' : desceu ? '#107c10' : C.textMuted
                          const seta = subiu ? '▲' : desceu ? '▼' : '='
                          return (
                            <div
                              title={`Preco actual no catalogo: ${fmt(r.preco_actual)} € s/IVA · delta ${fmt(r.delta_abs)} € (${fmt(r.delta_pct)}%)`}
                              style={{ fontSize: '10px', color: cor, marginTop: '2px', fontWeight: '600' }}
                            >
                              hoje: {fmt(r.preco_actual)} € <span style={{ marginLeft: '2px' }}>{seta} {fmt(Math.abs(r.delta_abs))} €</span>
                            </div>
                          )
                        })()}
                      </td>
                      <td style={{ ...tdBase, textAlign: 'right', fontFamily: 'monospace' }} title={tooltipUnit}>{fmt(r.preco_unit_com_iva)} €{r.is_interno && <span title="IVA 0% · interno">*</span>}</td>
                      <td style={{ ...tdBase, textAlign: 'right', fontFamily: 'monospace' }} title={tooltipTotal}>{fmt(r.total_sem_iva)} €</td>
                      <td style={{ ...tdBase, textAlign: 'right', fontFamily: 'monospace', fontWeight: '700', color: '#107c10' }} title={tooltipTotal}>{fmt(r.total_com_iva)} €{r.is_interno && <span title="IVA 0% · interno">*</span>}</td>
                      <td style={tdBase}>
                        <span style={{ display: 'inline-block', padding: '2px 8px', background: estadoBadge.bg, color: estadoBadge.fg, borderRadius: '10px', fontSize: '10px', fontWeight: '700' }}>{estadoBadge.txt}</span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        <p style={{ fontSize: '11px', color: C.textMuted, marginTop: '10px' }}>
          Precos apresentados sao <strong>efectivos</strong> — ja com o desconto da encomenda aplicado pro-rata (passa o rato para ver o preco tabela original antes do desconto).
          Por baixo do €/un s/IVA aparece o <strong>preco actual no catalogo</strong> (base s/IVA, sem portes/desconto do cliente): <span style={{ color: '#a4262c', fontWeight: '600' }}>▲ vermelho</span> se subiu &ge; 0.05 €, <span style={{ color: '#107c10', fontWeight: '600' }}>▼ verde</span> se desceu &ge; 0.05 €.
          Linhas destacadas a amarelo sao de <strong>clientes internos</strong> (IVA 0%, preco = custo, nao representam venda a lucrar).
        </p>
        </>)}
      </div>
    </div>
  )
}
